import React, { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Plus,
  MoreVertical,
  Play,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Loader2,
  AlertTriangle,
  Filter,
  Search,
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { AIAgent } from "./types";
import { calculateTotalTests } from "./utils";
import { visualizationData } from "./data/data";

const TopicRow: React.FC<{
  test: any;
  onViewDetails: (test: any) => void;
}> = ({ test, onViewDetails }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isDropdownOpen) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "passed":
        return <CheckCircle className="h-4 w-4" />;
      case "failed":
        return <XCircle className="h-4 w-4" />;
      case "running":
        return <Loader2 className="h-4 w-4 animate-spin" />;
      default:
        return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const getTopicColor = (topic: string) => {
    switch (topic) {
      case "Foundation":
        return "bg-green-900/30 text-green-400 border-green-600/30";
      case "Lipstick":
        return "bg-purple-900/30 text-purple-400 border-purple-600/30";
      case "Mascara":
        return "bg-blue-900/30 text-blue-400 border-blue-600/30";
      case "Eye Makeup":
        return "bg-yellow-900/30 text-yellow-400 border-yellow-600/30";
      default:
        return "bg-gray-900/30 text-gray-400 border-gray-600/30";
    }
  };

  const getCoverageColor = (coverage: number) => {
    if (coverage >= 80) return "text-green-400";
    if (coverage >= 60) return "text-yellow-400";
    return "text-red-400";
  };

  const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel) {
      case "low":
        return "bg-green-900/30 text-green-400 border-green-600/30";
      case "medium":
        return "bg-yellow-900/30 text-yellow-400 border-yellow-600/30";
      case "high":
        return "bg-red-900/30 text-red-400 border-red-600/30";
      default:
        return "bg-gray-900/30 text-gray-400 border-gray-600/30";
    }
  };

  const getRiskLevelIcon = (riskLevel: string) => {
    switch (riskLevel) {
      case "low":
        return <CheckCircle className="h-3 w-3" />;
      case "medium":
        return <AlertTriangle className="h-3 w-3" />;
      case "high":
        return <XCircle className="h-3 w-3" />;
      default:
        return <AlertTriangle className="h-3 w-3" />;
    }
  };

  // Calculate pass rate and risk level based on test data
  const totals = calculateTotalTests(test);
  const passRate = totals.total > 0 ? (totals.passed / totals.total) * 100 : 0;
  const riskLevel = passRate >= 80 ? "low" : passRate >= 60 ? "medium" : "high";

  return (
    <div
      className="flex items-center justify-between p-4 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-750 transition-colors mb-2 cursor-pointer"
      onClick={() => onViewDetails(test)}
    >
      <div className="flex items-center space-x-4 flex-1">
        {/* Topic Name and Description */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <h3 className="text-sm font-medium text-gray-100 truncate">
              {test.name}
            </h3>
            <Badge className={`text-xs ${getRiskLevelColor(riskLevel)}`}>
              <span className="mr-1">{getRiskLevelIcon(riskLevel)}</span>
              {riskLevel}
            </Badge>
          </div>
          <p className="text-xs text-gray-400 truncate">
            {test.children
              ? `${test.children.length} sub-topics`
              : "No sub-topics"}
          </p>
        </div>

        {/* Testing Metrics */}
        <div className="flex items-center space-x-6 text-xs">
          {/* Coverage */}
          <div className="text-center">
            <div
              className={`font-medium ${getCoverageColor(test.testCoverage || 0)}`}
            >
              {test.testCoverage || 0}%
            </div>
            <div className="text-gray-400">Coverage</div>
          </div>

          {/* Test Results */}
          <div className="text-center">
            <div className="text-gray-100 font-medium">
              {totals.passed}/{totals.total}
            </div>
            <div className="text-gray-400">Tests</div>
          </div>

          {/* Pass Rate */}
          <div className="text-center">
            <div className="text-gray-100 font-medium">
              {passRate.toFixed(1)}%
            </div>
            <div className="text-gray-400">Pass Rate</div>
          </div>

          {/* Sub-topics */}
          <div className="text-center">
            <div className="text-gray-100 font-medium">
              {test.children ? test.children.length : 0}
            </div>
            <div className="text-gray-400">Sub-topics</div>
          </div>
        </div>

        {/* Actions Dropdown */}
        <div className="relative" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 hover:bg-gray-600"
            onClick={(e) => {
              e.stopPropagation();
              setIsDropdownOpen(!isDropdownOpen);
            }}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
          {isDropdownOpen && (
            <div className="absolute right-0 top-full mt-1 w-32 bg-gray-800 border border-gray-600 rounded-md shadow-lg z-10">
              <button
                className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDropdownOpen(false);
                  // Handle run action
                }}
              >
                <Play className="h-4 w-4 mr-2" />
                Run Tests
              </button>
              <button
                className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDropdownOpen(false);
                  // Handle edit action
                }}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </button>
              <button
                className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-red-900/20 flex items-center"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDropdownOpen(false);
                  // Handle remove action
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Remove
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const TopicsTab: React.FC<{ agent: AIAgent }> = ({ agent }) => {
  const navigate = useNavigate();
  const [isCreateTestModalOpen, setIsCreateTestModalOpen] = useState(false);
  const [topics, setTopics] = useState(visualizationData.children || []);
  const [generatingTestId, setGeneratingTestId] = useState<string | null>(null);
  const [topicFilter, setTopicFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [newTestForm, setNewTestForm] = useState({
    name: "",
    description: "",
  });

  // Filter tests based on current filters
  const filteredTests = topics.filter((topic) => {
    const totals = calculateTotalTests(topic);
    const passRate =
      totals.total > 0 ? (totals.passed / totals.total) * 100 : 0;

    const matchesCoverage = (() => {
      switch (statusFilter) {
        case "high":
          return topic.testCoverage >= 80;
        case "medium":
          return topic.testCoverage >= 60 && topic.testCoverage < 80;
        case "low":
          return topic.testCoverage < 60;
        default:
          return true;
      }
    })();

    const matchesRiskLevel =
      topicFilter === "all" ||
      (passRate >= 80 ? "low" : passRate >= 60 ? "medium" : "high") ===
        topicFilter;

    const matchesSearch =
      searchQuery === "" ||
      topic.name.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCoverage && matchesRiskLevel && matchesSearch;
  });

  const handleViewTestDetails = (topic: any) => {
    navigate({ to: `/ai-agents/${agent.id}/topics/${topic.id}` });
  };

  return (
    <div>
      {/* Card containing filters and test rows */}
      <Card className="bg-gray-800 border border-gray-700">
        <CardContent className="p-0">
          {/* Filters inside Card */}
          <div className="px-4 lg:px-6 py-3 border-b border-gray-700">
            <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-4 lg:justify-between space-y-3 lg:space-y-0">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                <div className="flex items-center space-x-2">
                  <Filter className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-300">Filters:</span>
                </div>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                  {/* Coverage Filter */}
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-1 bg-gray-700 border border-gray-600 rounded-md text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="all">All Coverage</option>
                    <option value="high">High Coverage (≥80%)</option>
                    <option value="medium">Medium Coverage (60-79%)</option>
                    <option value="low">Low Coverage (&lt;60%)</option>
                  </select>
                  {/* Risk Level Filter */}
                  <select
                    value={topicFilter}
                    onChange={(e) => setTopicFilter(e.target.value)}
                    className="px-3 py-1 bg-gray-700 border border-gray-600 rounded-md text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="all">All Risk Levels</option>
                    <option value="low">Low Risk</option>
                    <option value="medium">Medium Risk</option>
                    <option value="high">High Risk</option>
                  </select>
                </div>
                <span className="text-sm text-gray-400">
                  {filteredTests.length} of {topics.length} topics
                </span>
              </div>
              <div className="relative flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search topics..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-1 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-gray-100 placeholder-gray-400"
                  />
                </div>
                {/* Add test button (DialogTrigger) */}
                <Dialog
                  open={isCreateTestModalOpen}
                  onOpenChange={setIsCreateTestModalOpen}
                >
                  <DialogTrigger asChild>
                    <Button className="bg-pink-400 hover:bg-pink-500">
                      <Plus className="h-4 w-4 mr-2" />
                      Add new topic
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-gray-800 border-gray-700 text-gray-100">
                    <DialogHeader>
                      <DialogTitle className="text-gray-100">
                        Create New Topic
                      </DialogTitle>
                      <DialogDescription className="text-gray-400">
                        Define a new functional area for testing the AI agent's
                        capabilities
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-300">
                          Topic Name
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., Customer Personalization"
                          value={newTestForm.name}
                          onChange={(e) =>
                            setNewTestForm({
                              ...newTestForm,
                              name: e.target.value,
                            })
                          }
                          className="w-full mt-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-300">
                          Description
                        </label>
                        <textarea
                          placeholder="Describe what this functional area covers and what aspects should be tested..."
                          rows={3}
                          value={newTestForm.description}
                          onChange={(e) =>
                            setNewTestForm({
                              ...newTestForm,
                              description: e.target.value,
                            })
                          }
                          className="w-full mt-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        className="border-gray-600 text-gray-300 hover:bg-gray-700"
                        onClick={() => setIsCreateTestModalOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="bg-pink-400 hover:bg-pink-500"
                        onClick={() => {
                          // Handle test creation logic here
                          console.log("Creating topic:", newTestForm);
                          setIsCreateTestModalOpen(false);
                          setNewTestForm({
                            name: "",
                            description: "",
                          });
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create Topic
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>
          {/* Unified Test List inside Card */}
          <div className="space-y-3 px-4 lg:px-6 py-4 lg:py-6">
            {filteredTests.map((topic) => (
              <TopicRow
                key={topic.id}
                test={topic}
                onViewDetails={handleViewTestDetails}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
